import React from 'react';
import {
  Zap, TrendingUp, Leaf, Trophy,
  Sparkles, Salad, Award, Dumbbell, Flame, X,
  Target, Compass, Crown, Shield, ChevronDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { MacroProgressCharts } from './MacroProgressCharts';
import { MicroVictoryBar } from './ui/MicroVictory';
import StreakDisplay from './StreakDisplay';
import AdaptiveChallengeCard from './AdaptiveChallengeCard';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';

// 🛡️ Defensive number coercion — API stats can occasionally come back as
// strings ("15.5") if the LLM stamped them into a meal doc before v138's
// backend coercion patch. Without this, rendering crashes the whole dashboard
// (caught by no boundary → user sees blank screen).
const safeNum = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// ─────────────────────────────────────────────────────────────────────────────
// GLOW card presets (Iter. 411.ll — Advanced Stats refresh)
// Each card gets a colored glass-morphism background + neon drop-shadow
// matching its semantic color (protein=orange, calories=red-orange,
// fiber=green, points=amber, badges=purple).
// ─────────────────────────────────────────────────────────────────────────────
const GLOW = {
  protein: {
    className: 'border-orange-500/30 hover:border-orange-400/60 transition-all',
    style: {
      background: 'linear-gradient(160deg, rgba(251,146,60,0.08) 0%, rgba(0,0,0,0.65) 100%)',
      backdropFilter: 'blur(14px)',
      boxShadow: '0 0 18px rgba(251,146,60,0.25), inset 0 1px 0 rgba(255,255,255,0.05)',
    },
  },
  calories: {
    className: 'border-red-500/30 hover:border-red-400/60 transition-all',
    style: {
      background: 'linear-gradient(160deg, rgba(239,68,68,0.08) 0%, rgba(0,0,0,0.65) 100%)',
      backdropFilter: 'blur(14px)',
      boxShadow: '0 0 18px rgba(239,68,68,0.22), inset 0 1px 0 rgba(255,255,255,0.05)',
    },
  },
  caloriesWorkout: {
    className: 'border-orange-500/50 transition-all',
    style: {
      background: 'linear-gradient(160deg, rgba(249,115,22,0.18) 0%, rgba(0,0,0,0.6) 100%)',
      backdropFilter: 'blur(14px)',
      boxShadow: '0 0 22px rgba(249,115,22,0.45), inset 0 1px 0 rgba(255,255,255,0.08)',
    },
  },
  fiber: {
    className: 'border-emerald-500/30 hover:border-emerald-400/60 transition-all',
    style: {
      background: 'linear-gradient(160deg, rgba(16,185,129,0.08) 0%, rgba(0,0,0,0.65) 100%)',
      backdropFilter: 'blur(14px)',
      boxShadow: '0 0 18px rgba(16,185,129,0.22), inset 0 1px 0 rgba(255,255,255,0.05)',
    },
  },
  points: {
    className: 'border-amber-400/40 hover:border-amber-300/70 transition-all',
    style: {
      background: 'linear-gradient(160deg, rgba(251,191,36,0.12) 0%, rgba(0,0,0,0.7) 100%)',
      backdropFilter: 'blur(14px)',
      boxShadow: '0 0 24px rgba(251,191,36,0.30), inset 0 1px 0 rgba(255,255,255,0.06)',
    },
  },
  badges: {
    className: 'border-purple-500/30 hover:border-purple-400/60 transition-all cursor-pointer',
    style: {
      background: 'linear-gradient(160deg, rgba(168,85,247,0.10) 0%, rgba(0,0,0,0.65) 100%)',
      backdropFilter: 'blur(14px)',
      boxShadow: '0 0 20px rgba(168,85,247,0.24), inset 0 1px 0 rgba(255,255,255,0.05)',
    },
  },
};

export const DashboardAdvancedStats = React.memo(function DashboardAdvancedStats({
  language, t, stats, effectiveCalorieTarget, calorieProgress,
  fiberProgress, fiberTarget, badges, weeklyData, tripleStats,
  showDetails, setShowDetails, setShowBadges, setShowWorkout, deleteWorkout
}) {
  const proteinHit = safeNum(stats?.today_protein_g) >= safeNum(stats?.protein_target_min);

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setShowDetails(!showDetails)}
        className="w-full mb-4 justify-between font-heading uppercase tracking-wide text-sm border-orange-500/30 bg-black/30 backdrop-blur-md hover:border-orange-400/60 hover:bg-orange-500/5 transition-all"
        style={{ boxShadow: '0 0 12px rgba(251,146,60,0.15)' }}
        data-testid="toggle-details-btn"
      >
        <span className="text-orange-200" style={{ textShadow: '0 0 6px rgba(251,146,60,0.4)' }}>
          {language === 'fr' ? 'Statistiques avancees' : 'Advanced stats'}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform duration-300 text-orange-300 ${showDetails ? 'rotate-180' : ''}`} />
      </Button>

      {showDetails && (
        <div>
        {/* Bento Grid — Iter. 411.ll: 4 core macro/points cards + badges */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
          {/* ───────── Protein Card (GLOW orange) ───────── */}
          <div className="md:col-span-2">
            <Card className={`h-full ${GLOW.protein.className}`} style={GLOW.protein.style}>
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2 text-orange-100"
                  style={{ textShadow: '0 0 8px rgba(251,146,60,0.5)' }}>
                  <Zap className="w-5 h-5 text-orange-400" style={{ filter: 'drop-shadow(0 0 4px rgba(251,146,60,0.7))' }} />
                  {t('dashboard.protein')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-4 mb-4">
                  <span className={`font-heading text-5xl font-black transition-colors ${proteinHit ? 'text-amber-300' : 'text-orange-400'}`}
                    style={{ textShadow: proteinHit ? '0 0 14px rgba(252,211,77,0.7)' : '0 0 10px rgba(251,146,60,0.5)' }}>
                    {safeNum(stats?.today_protein_g).toFixed(0)}g
                  </span>
                  <span className="text-orange-200/60 pb-2">
                    / {safeNum(stats?.protein_target_min).toFixed(0)}-{safeNum(stats?.protein_target_max).toFixed(0)}g
                  </span>
                </div>
                <MicroVictoryBar
                  value={safeNum(stats?.today_protein_g)}
                  target={safeNum(stats?.protein_target_min) || 100}
                  label={language === 'fr' ? 'Vers la cible Poids Santé' : 'Toward healthy-weight goal'}
                  unit="g"
                  pulseWhenHit
                  language={language}
                  testId="protein-glow-bar"
                />
              </CardContent>
            </Card>
          </div>

          {/* ───────── Calories Card (GLOW red / orange when workout) ───────── */}
          <div className="md:col-span-2">
            <Card
              className={`h-full ${stats?.has_workout_today ? GLOW.caloriesWorkout.className : GLOW.calories.className}`}
              style={stats?.has_workout_today ? GLOW.caloriesWorkout.style : GLOW.calories.style}
            >
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2 text-red-100"
                  style={{ textShadow: '0 0 8px rgba(239,68,68,0.5)' }}>
                  <TrendingUp className="w-5 h-5 text-red-400" style={{ filter: 'drop-shadow(0 0 4px rgba(239,68,68,0.7))' }} />
                  {t('dashboard.calories')}
                  {stats?.has_workout_today && (
                    <span className="ml-auto flex items-center gap-1 text-xs font-normal text-orange-200 bg-orange-500/20 border border-orange-400/40 px-2 py-1 rounded-full"
                      style={{ boxShadow: '0 0 10px rgba(249,115,22,0.4)' }}>
                      <Dumbbell className="w-3 h-3" />
                      +{stats?.workout_bonus_calories}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-2 mb-4">
                  <span className="font-heading text-4xl font-black text-white"
                    style={{ textShadow: '0 0 10px rgba(239,68,68,0.4)' }}>
                    {safeNum(stats?.today_calories).toFixed(0)}
                  </span>
                  <span className="text-red-200/60 pb-1">/ {safeNum(effectiveCalorieTarget).toFixed(0)}</span>
                </div>
                <Progress value={calorieProgress} className="h-3 bg-black/50" data-testid="calorie-progress" />
                <div className="mt-4 pt-4 border-t border-red-500/20">
                  {stats?.has_workout_today ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm">
                        <Flame className="w-4 h-4 text-orange-400" style={{ filter: 'drop-shadow(0 0 4px rgba(251,146,60,0.7))' }} />
                        <span className="text-orange-300 font-medium">
                          {stats?.today_workout?.intensity === 'light' && (language === 'fr' ? 'Leger' : 'Light')}
                          {stats?.today_workout?.intensity === 'moderate' && (language === 'fr' ? 'Modere' : 'Moderate')}
                          {stats?.today_workout?.intensity === 'intense' && 'Intense'}
                          {stats?.today_workout?.intensity === 'extreme' && (language === 'fr' ? 'Extreme' : 'Extreme')}
                        </span>
                        <span className="text-white/50">
                          {'\u2022'} {stats?.today_workout?.duration_minutes} min
                        </span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={deleteWorkout} className="text-white/50 hover:text-red-400">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline" size="sm"
                      onClick={() => setShowWorkout(true)}
                      className="w-full border-dashed border-orange-500/50 text-orange-300 hover:bg-orange-500/10 hover:border-orange-400"
                      data-testid="log-workout-btn"
                    >
                      <Dumbbell className="w-4 h-4 mr-2" />
                      {language === 'fr' ? "Entrainement aujourd'hui?" : 'Workout today?'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ───────── Fiber Card (GLOW emerald) ───────── */}
          <div className="md:col-span-2">
            <Card className={`h-full ${GLOW.fiber.className}`} style={GLOW.fiber.style}>
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2 text-emerald-100"
                  style={{ textShadow: '0 0 8px rgba(16,185,129,0.5)' }}>
                  <Leaf className="w-5 h-5 text-emerald-400" style={{ filter: 'drop-shadow(0 0 4px rgba(16,185,129,0.7))' }} />
                  {language === 'fr' ? 'Fibres' : 'Fiber'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-2 mb-4">
                  <span className="font-heading text-4xl font-black text-emerald-300"
                    style={{ textShadow: '0 0 12px rgba(16,185,129,0.5)' }}>
                    {safeNum(stats?.today_fiber_g).toFixed(0)}g
                  </span>
                  <span className="text-emerald-200/60 pb-1">/ {fiberTarget}g</span>
                </div>
                <Progress value={fiberProgress} className="h-3 bg-black/50" data-testid="fiber-progress" />
                <p className="text-sm text-emerald-200/70 mt-2">
                  {fiberProgress >= 100
                    ? (language === 'fr' ? 'Objectif atteint!' : 'Goal reached!')
                    : (language === 'fr' ? `${(fiberTarget - safeNum(stats?.today_fiber_g)).toFixed(0)}g restants` : `${(fiberTarget - safeNum(stats?.today_fiber_g)).toFixed(0)}g remaining`)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* ───────── Points Card + Grade (GLOW amber) ───────── */}
          <div className="md:col-span-2">
            <Card className={`h-full ${GLOW.points.className}`} style={GLOW.points.style}>
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2 text-amber-100"
                  style={{ textShadow: '0 0 8px rgba(251,191,36,0.6)' }}>
                  <Trophy className="w-5 h-5 text-amber-400" style={{ filter: 'drop-shadow(0 0 5px rgba(251,191,36,0.8))' }} />
                  {language === 'fr' ? 'Points YUMIX' : 'YUMIX Points'}
                  {tripleStats?.grade && (
                    <span
                      data-testid="dashboard-grade-badge"
                      className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-heading uppercase tracking-wider rounded-full border"
                      style={{
                        borderColor: tripleStats.grade.color,
                        color: tripleStats.grade.color,
                        backgroundColor: `${tripleStats.grade.color}20`,
                        boxShadow: `0 0 12px ${tripleStats.grade.color}55`,
                      }}
                    >
                      {tripleStats.grade.id === 'legende' && <Crown className="w-3 h-3" />}
                      {tripleStats.grade.id === 'elite' && <Zap className="w-3 h-3" />}
                      {tripleStats.grade.id === 'gardien' && <Shield className="w-3 h-3" />}
                      {tripleStats.grade.name_fr}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-end gap-2">
                      <span className="font-heading text-5xl font-black text-amber-300"
                        style={{ textShadow: '0 0 16px rgba(251,191,36,0.6)' }}>
                        {stats?.total_points || 0}
                      </span>
                      <span className="text-amber-200/70 pb-2 font-bold">pts</span>
                    </div>
                    <p className="text-sm text-amber-100/60 mt-1">
                      +{stats?.weekly_points || 0} {language === 'fr' ? 'cette semaine' : 'this week'}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 text-sm text-amber-100/70">
                      <Sparkles className="w-4 h-4 text-amber-400" />
                      {language === 'fr' ? "Aujourd'hui" : 'Today'}: +{stats?.today_points || 0}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-emerald-200/80 mt-1">
                      <Salad className="w-4 h-4 text-emerald-400" />
                      {language === 'fr' ? 'Legumes' : 'Vegetables'}: {safeNum(stats?.today_vegetables_g).toFixed(0)}g
                    </div>
                  </div>
                </div>
                {tripleStats && (
                  <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-amber-400/20" data-testid="dashboard-triple-stats">
                    <div className="flex items-center gap-1.5 p-2 rounded-lg bg-black/40 border border-orange-500/20"
                      style={{ boxShadow: 'inset 0 0 8px rgba(251,146,60,0.10)' }}>
                      <Flame className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-black text-sm text-orange-200 leading-none">{tripleStats.constancy_pts || 0}</p>
                        <p className="text-[9px] text-white/50 truncate">Discipline</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 p-2 rounded-lg bg-black/40 border border-amber-500/20"
                      style={{ boxShadow: 'inset 0 0 8px rgba(251,191,36,0.10)' }}>
                      <Target className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-black text-sm text-amber-200 leading-none">{tripleStats.performance_pts || 0}</p>
                        <p className="text-[9px] text-white/50 truncate">{language === 'fr' ? 'Sommet' : 'Summit'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 p-2 rounded-lg bg-black/40 border border-blue-500/20"
                      style={{ boxShadow: 'inset 0 0 8px rgba(59,130,246,0.10)' }}>
                      <Compass className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-black text-sm text-blue-200 leading-none">{tripleStats.engagement_pts || 0}</p>
                        <p className="text-[9px] text-white/50 truncate">{language === 'fr' ? 'Eclaireur' : 'Scout'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* ───────── Badges Card (GLOW purple) ───────── */}
          <div className="md:col-span-2">
            <Card
              className={`h-full ${GLOW.badges.className}`}
              style={GLOW.badges.style}
              onClick={() => setShowBadges(true)}
              data-testid="badges-card"
            >
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2 text-purple-100"
                  style={{ textShadow: '0 0 8px rgba(168,85,247,0.5)' }}>
                  <Award className="w-5 h-5 text-purple-400" style={{ filter: 'drop-shadow(0 0 4px rgba(168,85,247,0.7))' }} />
                  Badges
                  <span className="text-sm font-normal text-purple-200/70 ml-2">
                    {badges?.total_earned || 0}/{badges?.total_available || 0}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {badges?.earned?.length > 0 ? (
                  <div className="flex flex-wrap gap-3">
                    {badges.earned.slice(0, 5).map((badge) => (
                      <div
                        key={badge.id}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg transition-transform hover:scale-105"
                        style={{
                          backgroundColor: `${badge.color}25`,
                          border: `1px solid ${badge.color}60`,
                          boxShadow: `0 0 10px ${badge.color}40`,
                        }}
                        title={badge.description}
                      >
                        <span className="text-xl" style={{ filter: `drop-shadow(0 0 6px ${badge.color}80)` }}>{badge.icon}</span>
                        <span className="text-sm font-medium hidden sm:inline" style={{ color: badge.color, textShadow: `0 0 6px ${badge.color}80` }}>{badge.name}</span>
                      </div>
                    ))}
                    {badges.earned.length > 5 && (
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-purple-500/20 border border-purple-400/40 text-purple-200 text-sm font-bold"
                        style={{ boxShadow: '0 0 10px rgba(168,85,247,0.35)' }}>
                        +{badges.earned.length - 5}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-purple-200/60 text-sm">
                      {language === 'fr' ? 'Photographier ton premier repas pour débloquer des badges!' : 'Log your first meal to unlock badges!'}
                    </p>
                  </div>
                )}
                {badges?.progress?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-purple-400/20">
                    <p className="text-xs text-purple-200/70 mb-2 uppercase tracking-wide">{language === 'fr' ? 'Prochain badge' : 'Next badge'}</p>
                    <div className="flex items-center gap-3">
                      <span className="text-lg opacity-60" style={{ filter: 'drop-shadow(0 0 4px rgba(168,85,247,0.4))' }}>{badges.progress[0]?.icon}</span>
                      <div className="flex-1">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-purple-100/80">{badges.progress[0]?.name}</span>
                          <span className="text-purple-200/60">{badges.progress[0]?.current}/{badges.progress[0]?.target}</span>
                        </div>
                        <Progress value={badges.progress[0]?.progress_percent || 0} className="h-2 bg-black/50" />
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* ───────── Weekly Chart (already GLOW dark neon) ───────── */}
          <div className="md:col-span-4">
            <Card
              className="border-orange-400/30 h-full transition-colors"
              style={{
                background: 'linear-gradient(180deg, #000 0%, #0a0a0a 100%)',
                boxShadow: '0 0 14px rgba(251,146,60,0.32), inset 0 0 0 1px rgba(251,146,60,0.10)',
              }}
            >
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-xl uppercase tracking-tight text-orange-200"
                  style={{ textShadow: '0 0 8px rgba(251,146,60,0.5)' }}>
                  {t('dashboard.weekly')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={weeklyData?.data || []}>
                      <defs>
                        <linearGradient id="bar-orange-glow" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#fbbf24" stopOpacity={1} />
                          <stop offset="100%" stopColor="#f97316" stopOpacity={0.95} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(251,146,60,0.10)" />
                      <XAxis dataKey="day" stroke="rgba(251,191,36,0.6)" tick={{ fill: 'rgba(251,191,36,0.7)', fontSize: 11 }} />
                      <YAxis stroke="rgba(251,191,36,0.5)" tick={{ fill: 'rgba(251,191,36,0.6)', fontSize: 11 }} />
                      <Tooltip contentStyle={{ backgroundColor: '#000', border: '1px solid rgba(251,146,60,0.4)', borderRadius: '8px', boxShadow: '0 0 12px rgba(251,146,60,0.4)' }} labelStyle={{ color: '#fbbf24' }} />
                      <ReferenceLine y={weeklyData?.targets?.protein_min || 100} stroke="#fbbf24" strokeDasharray="3 3" label={{ value: 'Min', fill: '#fbbf24' }} />
                      <Bar dataKey="protein_g" fill="url(#bar-orange-glow)" radius={[4, 4, 0, 0]} name={t('dashboard.protein')} style={{ filter: 'drop-shadow(0 0 6px rgba(251,146,60,0.6))' }} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Macro Progress Charts (7j / 30j) */}
        <div className="mt-6"><MacroProgressCharts /></div>

        {/* Adaptive Challenge + Streak Display
            Note (Iter. 411.ll): ChallengesWidget removed — redundant with
            "Défis de la Semaine" (WeeklyQuestCard) already at top of Dashboard. */}
        <div className="mt-6 space-y-6">
          <AdaptiveChallengeCard />
          <StreakDisplay />
        </div>
        </div>
      )}
    </>
  );
});
